import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { safeDecrypt } from "./uwin-decrypt";

/**
 * SessionSummaryDialog Component
 * Displays the statistics/summary of a completed session.
 */
const SessionSummaryDialog = ({ open, onClose, summaryData, sessionInfo }) => {
  if (!summaryData) return null;

  // Format label: Replace underscores with hyphens and uppercase
  // Examples: td_1 -> TD-1, vit_A_1 -> VIT-A-1, pcv_booster -> PCV-BOOSTER
  const formatLabel = (key) => {
    return key.toUpperCase().replace(/_/g, '-');
  };

  // Detect HPV/Adolescent session — Rule 1 (checked first, takes priority)
  // (same logic used by formatSessionNameDisplay / formatAgeRangeDisplay helpers)
  const isHPVSession = (() => {
    try {
      return safeDecrypt(sessionInfo?.uip_session_category_id) === "4";
    } catch {
      return false;
    }
  })();

  // Detect Campaign session — Rule 2 (only applies when Rule 1 is false)
  const isCampaignSession = !isHPVSession && sessionInfo?.is_campaign_session === true;

  // ── RI demographics ────────────────────────────────────────────────────────
  const demographicsKeys = [
    "preg_vaccinated",
    "infant_vaccinated",
    "children_vaccinated",
    "adolscent_vaccinated"
  ];

  const labelMap = {
    "preg_vaccinated": "Number of Pregnant Women vaccinated",
    "infant_vaccinated": "Number of Infants (0-1 year) vaccinated",
    "children_vaccinated": "Number of Children (>1 year) vaccinated",
    "adolscent_vaccinated": "Number of Adolescents (10-17 years) Vaccinated"
  };

  // ── HPV demographic key & label ────────────────────────────────────────────
  const HPV_DEMOGRAPHIC_KEY = "hpv_vaccinated";
  const HPV_DEMOGRAPHIC_LABEL = "Number of HPV vaccines administered (ages >=14 & 15Y 90D)";

  // ── Campaign demographic key & label ───────────────────────────────────────
  const CAMPAIGN_DEMOGRAPHIC_KEY = "campaign_vaccinated";
  const CAMPAIGN_DEMOGRAPHIC_LABEL = "Number of Beneficiaries administered";

  // ── Vaccine list (excludes whichever key is shown in the demographics row) ─
  const vaccineList = [];

  Object.entries(summaryData).forEach(([key, value]) => {
    let isExcluded;
    if (isHPVSession) {
      isExcluded = key === HPV_DEMOGRAPHIC_KEY;        // HPV: exclude hpv_vaccinated
    } else if (isCampaignSession) {
      isExcluded = key === CAMPAIGN_DEMOGRAPHIC_KEY;   // Campaign: exclude campaign_vaccinated
    } else {
      isExcluded = demographicsKeys.includes(key);     // RI: exclude the 4 standard keys
    }
    if (!isExcluded) {
      vaccineList.push({
        name: formatLabel(key),
        count: parseInt(value, 10) || 0
      });
    }
  });

  // Split only needed for the RI two-column layout
  const midPoint = Math.ceil(vaccineList.length / 2);
  const leftColumnVaccines = vaccineList.slice(0, midPoint);
  const rightColumnVaccines = vaccineList.slice(midPoint);

  const displayDate = sessionInfo?.date_of_session 
    ? dayjs(sessionInfo.date_of_session).format('DD.MM.YYYY')
    : "20.02.2026";

  // Extract from & to from display_time formatted like "25 Feb 2026 (08:00 AM - 05:15 PM)" if available
  let fromTime = "8:00 AM";
  let toTime = "5:15 PM";
  
  if (sessionInfo?.display_time) {
      // Extract everything between parenthesis
      const timeMatch = sessionInfo.display_time.match(/\((.+?)\)/);
      if (timeMatch && timeMatch[1]) {
          const timeParts = timeMatch[1].split("-");
          if (timeParts.length === 2) {
              fromTime = timeParts[0].trim();
              toTime = timeParts[1].trim();
          }
      }
  }

  const sessionType = sessionInfo?.uip_session_category || "RI - Children And Pregnant Women";

  const handleDownload = () => {
    // Download logic placeholder
    console.log("Downloading Session Summary");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '80vh', borderRadius: 2 } }}>
      <DialogTitle sx={{ pt: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box />
        {/* <Button 
          variant="contained" 
          disableElevation
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ 
            backgroundColor: '#005af0',
            borderRadius: 8, 
            textTransform: 'none', 
            fontWeight: 600, 
            px: 3,
            py: 1,
            '&:hover': {
              backgroundColor: '#004bdc'
            }
          }}
        >
          DOWNLOAD SUMMARY
        </Button> */}
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ px: 4, pb: 4 }}>
        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '20px' }}>
                SESSION COVERAGE SUMMARY
                </Typography>
            </Box>
            
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="body1" sx={{ fontWeight: 700, mb: 1.5, fontSize: '15px' }}>
                Date: {displayDate}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, mb: 1.5, fontSize: '15px' }}>
                Session Timing - From: {fromTime} To: {toTime}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '15px' }}>
                Session Type: {sessionType}
                </Typography>
            </Box>

            {/* ── Demographics / count section ───────────────────────────── */}
            <TableContainer sx={{ borderBottom: "1px solid #e0e0e0" }}>
                <Table size="small">
                    <TableBody>
                        {isHPVSession ? (
                            // Rule 1 — HPV: single row for hpv_vaccinated
                            <TableRow>
                                <TableCell sx={{ pl: 2, borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                    {HPV_DEMOGRAPHIC_LABEL}
                                </TableCell>
                                <TableCell align="center" sx={{ width: '25%', borderLeft: "1px solid #e0e0e0", borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                    {summaryData[HPV_DEMOGRAPHIC_KEY] || 0}
                                </TableCell>
                            </TableRow>
                        ) : isCampaignSession ? (
                            // Rule 2 — Campaign: single row for campaign_vaccinated
                            <TableRow>
                                <TableCell sx={{ pl: 2, borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                    {CAMPAIGN_DEMOGRAPHIC_LABEL}
                                </TableCell>
                                <TableCell align="center" sx={{ width: '25%', borderLeft: "1px solid #e0e0e0", borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                    {summaryData[CAMPAIGN_DEMOGRAPHIC_KEY] || 0}
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Rule 3 — RI: standard 4 demographic rows
                            demographicsKeys.map(key => (
                                <TableRow key={key}>
                                    <TableCell sx={{ pl: 2, borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                        {labelMap[key]}
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: '25%', borderLeft: "1px solid #e0e0e0", borderBottom: "none", py: 1.5, fontSize: '14px' }}>
                                        {summaryData[key] || 0}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── Vaccine dose table ─────────────────────────────────────── */}
            <TableContainer>
                {(isHPVSession || isCampaignSession) ? (
                    // Rule 1 & 2 — HPV / Campaign: single-column layout (no split)
                    <Table size="small" sx={{ borderCollapse: 'collapse', '& td, & th': { borderBottom: '1px solid #e0e0e0' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, pl: 2, width: '50%', pt: 2, pb: 2, fontSize: '14px' }}>Vaccine Dose</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '50%', pt: 2, pb: 2, fontSize: '14px' }}>Number of doses administered in Session</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vaccineList.map((item, index) => (
                                <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                    <TableCell sx={{ pl: 2, py: 1.5, fontSize: '14px' }}>{item.name}</TableCell>
                                    <TableCell sx={{ py: 1.5, fontSize: '14px' }}>{item.count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    // Rule 3 — RI: two-column split layout
                    <Table size="small" sx={{ borderCollapse: 'collapse', '& td, & th': { borderBottom: '1px solid #e0e0e0' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, pl: 2, width: '25%', pt: 2, pb: 2, fontSize: '14px' }}>Vaccine Dose</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '25%', pt: 2, pb: 2, fontSize: '14px' }}>Number of doses administered in Session</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '25%', borderLeft: "1px solid #e0e0e0", pt: 2, pb: 2, fontSize: '14px' }}>Vaccine Dose</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '25%', pt: 2, pb: 2, fontSize: '14px' }}>Number of doses administered in Session</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leftColumnVaccines.map((item, index) => {
                                const rightItem = rightColumnVaccines[index];
                                return (
                                    <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                        <TableCell sx={{ pl: 2, py: 1.5, fontSize: '14px' }}>{item.name}</TableCell>
                                        <TableCell sx={{ py: 1.5, fontSize: '14px' }}>{item.count}</TableCell>
                                        <TableCell sx={{ borderLeft: "1px solid #e0e0e0", py: 1.5, fontSize: '14px' }}>{rightItem ? rightItem.name : ""}</TableCell>
                                        <TableCell sx={{ py: 1.5, fontSize: '14px' }}>{rightItem ? rightItem.count : ""}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SessionSummaryDialog;
